import { createRootRoute, createRoute, createRouter, Link, Outlet, redirect } from '@tanstack/react-router';
import { currentUser } from './auth/userManager';
import { EmptyState } from './components/EmptyState';
import { Button } from './components/ui/button';
import { AppShell } from './shell/AppShell';
import { ACCESS_TABS, AccessPage, type AccessSearch } from './routes/AccessPage';
import { APP_TABS, AppPage, type AppPageSearch } from './routes/AppPage';
import { AcceptInvitationPage } from './routes/AcceptInvitationPage';
import { AuthCallback } from './routes/AuthCallback';
import { ClientsPage } from './routes/ClientsPage';
import { ClientDetailPage, clientDetailSearch } from './routes/ClientDetailPage';
import { TutorialsPage } from './routes/TutorialsPage';
import { ArchitecturePage } from './routes/ArchitecturePage';
import { PlatformProvidersPage } from './routes/PlatformProvidersPage';
import { BuildDetailPage } from './routes/BuildDetailPage';
import { BuildsPage } from './routes/BuildsPage';
import { CredentialsPage } from './routes/CredentialsPage';
import { DevicesPage } from './routes/DevicesPage';
import { EnvVarsPage } from './routes/EnvVarsPage';
import { GettingStartedPage, type GettingStartedSearch } from './routes/GettingStartedPage';
import { IntegrationsPage } from './routes/IntegrationsPage';
import { Overview } from './routes/Overview';
import { ProjectPage } from './routes/ProjectPage';
import { PROJECT_SETTINGS_SECTIONS, ProjectSettingsPage, type ProjectSettingsSearch } from './routes/ProjectSettingsPage';
import { ProjectsIndex } from './routes/ProjectsIndex';
import { monthParam } from './features/usage/usageModel';
import { projectSearchOf } from './features/apps/appsSectionModel';
import { SignIn } from './routes/SignIn';
import { UsagePage, type UsageSearch } from './routes/UsagePage';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/** Search params of the list pages; every key optional so links may omit them. */
export interface BuildsSearch {
  app?: string;
  environment?: string;
  platform?: string;
  /** Comma-separated statuses. */
  status?: string;
  branch?: string;
  /** Search by reference `app#number`. */
  q?: string;
}
/**
 * Apps & profiles: `?app=<appSlug>` selects an app (the selection is kept in the URL). The shadow mode deep link of the
 * worker's Slack report and of `ShadowRun.url` is `/projects/<projectSlug>?app=<appSlug>#shadow`.
 */
export interface ProjectSearch {
  app?: string;
  /** Opens Connect app once, then leaves the URL. */
  connect?: true;
}
export interface EnvSearch {
  scope?: string;
  app?: string;
  environment?: string;
  platform?: string;
  tab?: string;
}

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
  beforeLoad: async () => {
    if (await currentUser()) throw redirect({ to: '/' });
  },
  component: SignIn,
});

const callbackRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/callback', component: AuthCallback });
/** The invitation email's link (`#token=…`): outside the app shell, the invitee belongs to no org until it is accepted. */
const acceptInvitationRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invitations/accept', component: AcceptInvitationPage });

/** Everything below needs a session; without one the router sends the browser to /sign-in (and back afterwards). */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ location }) => {
    if (!(await currentUser())) throw redirect({ to: '/sign-in', search: { redirect: location.href, reason: undefined } });
  },
  component: AppShell,
});

const overviewRoute = createRoute({ getParentRoute: () => authedRoute, path: '/', component: Overview });
const projectsIndexRoute = createRoute({ getParentRoute: () => authedRoute, path: '/projects', component: ProjectsIndex });
const projectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$slug',
  validateSearch: (search: Record<string, unknown>): ProjectSearch => projectSearchOf(search),
  component: ProjectPage,
});

/** Project settings (design 28): one page of cards; `?section=` scrolls to one (general when absent). */
const projectSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$slug/settings',
  validateSearch: (search: Record<string, unknown>): ProjectSettingsSearch => ({
    section: typeof search.section === 'string' && (PROJECT_SETTINGS_SECTIONS as readonly string[]).includes(search.section) ? (search.section as ProjectSettingsSearch['section']) : undefined,
  }),
  component: ProjectSettingsPage,
});
/** The App page (design 29): `?tab=overview|profiles|env|shadow|danger` (overview when absent). */
const appRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$slug/apps/$app',
  validateSearch: (search: Record<string, unknown>): AppPageSearch => ({
    tab: typeof search.tab === 'string' && (APP_TABS as readonly string[]).includes(search.tab) ? (search.tab as AppPageSearch['tab']) : undefined,
  }),
  component: AppPage,
});

/** Builds list filters live in the URL so a filtered view can be shared and reloaded. */
const buildsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$slug/builds',
  validateSearch: (search: Record<string, unknown>): BuildsSearch => {
    const str = (k: string) => (typeof search[k] === 'string' && search[k] ? (search[k] as string) : undefined);
    return { app: str('app'), environment: str('environment'), platform: str('platform'), status: str('status'), branch: str('branch'), q: str('q') };
  },
  component: BuildsPage,
});
const buildDetailRoute = createRoute({ getParentRoute: () => authedRoute, path: '/projects/$slug/builds/$buildId', component: BuildDetailPage });
const envVarsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects/$slug/env',
  validateSearch: (search: Record<string, unknown>): EnvSearch => {
    const str = (k: string) => (typeof search[k] === 'string' && search[k] ? (search[k] as string) : undefined);
    return { scope: str('scope'), app: str('app'), environment: str('environment'), platform: str('platform'), tab: str('tab') };
  },
  component: EnvVarsPage,
});
/** Apple signing material of the project's iOS apps (custodied by Malible; operators manage it). */
const credentialsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/projects/$slug/credentials', component: CredentialsPage });
/** iOS test devices of the org (SPEC decision 28): registered by QR code, for internal iOS builds. */
const devicesRoute = createRoute({ getParentRoute: () => authedRoute, path: '/devices', component: DevicesPage });
const integrationsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/integrations', component: IntegrationsPage });
/** Client organizations, for platform operators (Malible staff) only. */
const clientsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/clients', component: ClientsPage });
/** One client (operators only, SPEC §17.6): details, people, status and danger zone; `?section=` lands on a card. */
const clientDetailRoute = createRoute({ getParentRoute: () => authedRoute, path: '/clients/$orgId', validateSearch: clientDetailSearch, component: ClientDetailPage });
const architectureRoute = createRoute({ getParentRoute: () => authedRoute, path: '/architecture', component: ArchitecturePage });
/** Hangar administration → Expo & Bitrise: Malible's own provider tokens (SPEC decision 19, operators only). */
const platformProvidersRoute = createRoute({ getParentRoute: () => authedRoute, path: '/platform-providers', component: PlatformProvidersPage });
const tutorialsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/tutorials', component: TutorialsPage });
/** Org-level access: `?tab=tokens|ci|members` (tokens when absent). */
const accessRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/access',
  validateSearch: (search: Record<string, unknown>): AccessSearch => ({
    tab: typeof search.tab === 'string' && (ACCESS_TABS as readonly string[]).includes(search.tab) ? search.tab : undefined,
  }),
  component: AccessPage,
});
/** Org-level usage and plan: `?month=YYYY-MM` (UTC; the current month when absent). */
const usageRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/usage',
  validateSearch: (search: Record<string, unknown>): UsageSearch => ({ month: monthParam(search.month) }),
  component: UsagePage,
});
const gettingStartedRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/getting-started',
  validateSearch: (search: Record<string, unknown>): GettingStartedSearch => {
    const str = (k: string) => (typeof search[k] === 'string' && search[k] ? (search[k] as string) : undefined);
    return { project: str('project'), app: str('app') };
  },
  component: GettingStartedPage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  callbackRoute,
  acceptInvitationRoute,
  authedRoute.addChildren([overviewRoute, projectsIndexRoute, projectRoute, projectSettingsRoute, appRoute, buildsRoute, buildDetailRoute, envVarsRoute, credentialsRoute, devicesRoute, integrationsRoute, gettingStartedRoute, accessRoute, usageRoute, clientsRoute, clientDetailRoute, tutorialsRoute, architectureRoute, platformProvidersRoute]),
]);

function NotFound() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <EmptyState
        icon="error"
        title="Page not found"
        description="Nothing lives at this address."
        action={
          <Button asChild variant="outline">
            <Link to="/">Back to Overview</Link>
          </Button>
        }
      />
    </div>
  );
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultNotFoundComponent: NotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
